/**
 * Storage Service
 * 
 * Handles all Supabase Storage operations for gallery images.
 * Manages image uploads, deletions, and URL generation.
 */

import { supabase, handleSupabaseError } from '../config/supabaseClient';

// Storage bucket name
const BUCKET_NAME = 'gallery-images';

/**
 * Upload an image file to Supabase Storage
 * @param {File} file - Image file from file input
 * @param {string} categoryName - Category name for organizing files
 * @returns {Promise<{url: string|null, error: Object|null}>}
 */
export const uploadImage = async (file, categoryName) => {
    try {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            return {
                url: null,
                error: { message: 'File must be an image' },
            };
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            return {
                url: null,
                error: { message: 'Image must be smaller than 5MB' },
            };
        }

        // Generate unique filename: category/timestamp-randomhex.extension
        const timestamp = Date.now();
        const randomHex = Math.random().toString(36).substring(2, 15);
        const extension = file.name.split('.').pop();
        const sanitizedCategory = categoryName.toLowerCase().replace(/[^a-z0-9]/g, '-');
        const filePath = `${sanitizedCategory}/${timestamp}-${randomHex}.${extension}`;

        // Upload file to Supabase Storage
        const { data, error } = await supabase.storage
            .from(BUCKET_NAME)
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false,
            });

        if (error) {
            return { url: null, error: handleSupabaseError(error) };
        }

        // Get public URL for the uploaded file
        const { data: { publicUrl } } = supabase.storage
            .from(BUCKET_NAME)
            .getPublicUrl(data.path);

        return { url: publicUrl, error: null };
    } catch (err) {
        return { url: null, error: handleSupabaseError(err) };
    }
};

/**
 * Delete an image from Supabase Storage
 * @param {string} imageUrl - Full public URL of the image
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const deleteImage = async (imageUrl) => {
    try {
        // Extract file path from URL
        // Example URL: https://xyz.supabase.co/storage/v1/object/public/gallery-images/category/file.jpg
        const urlParts = imageUrl.split('/storage/v1/object/public/gallery-images/');
        if (urlParts.length < 2) {
            return {
                success: false,
                error: { message: 'Invalid image URL format' },
            };
        }

        const filePath = urlParts[1];

        // Delete file from storage
        const { error } = await supabase.storage
            .from(BUCKET_NAME)
            .remove([filePath]);

        if (error) {
            return { success: false, error: handleSupabaseError(error) };
        }

        return { success: true, error: null };
    } catch (err) {
        return { success: false, error: handleSupabaseError(err) };
    }
};

/**
 * Get public URL for an image path
 * @param {string} filePath - Path within the bucket (e.g., "category/file.jpg")
 * @returns {string} - Public URL
 */
export const getImageUrl = (filePath) => {
    const { data } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(filePath);

    return data.publicUrl;
};

/**
 * List all files in a category folder
 * @param {string} categoryName - Category name
 * @returns {Promise<{files: Array, error: Object|null}>}
 */
export const listImagesInCategory = async (categoryName) => {
    try {
        const sanitizedCategory = categoryName.toLowerCase().replace(/[^a-z0-9]/g, '-');

        const { data, error } = await supabase.storage
            .from(BUCKET_NAME)
            .list(sanitizedCategory, {
                limit: 100,
                offset: 0,
                sortBy: { column: 'created_at', order: 'desc' },
            });

        if (error) {
            return { files: null, error: handleSupabaseError(error) };
        }

        return { files: data, error: null };
    } catch (err) {
        return { files: null, error: handleSupabaseError(err) };
    }
};

/**
 * Create bucket if it doesn't exist (admin setup only)
 * NOTE: This requires service_role key, not anon key
 * Use Supabase dashboard to create bucket instead
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const createBucketIfNotExists = async () => {
    try {
        // Check if bucket exists
        const { data: buckets, error: listError } = await supabase.storage.listBuckets();

        if (listError) {
            return { success: false, error: handleSupabaseError(listError) };
        }

        const bucketExists = buckets.some(bucket => bucket.name === BUCKET_NAME);

        if (bucketExists) {
            return { success: true, error: null };
        }

        // Create bucket with public access
        const { error: createError } = await supabase.storage.createBucket(BUCKET_NAME, {
            public: true,
            fileSizeLimit: 5242880, // 5MB
            allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        });

        if (createError) {
            return { success: false, error: handleSupabaseError(createError) };
        }

        return { success: true, error: null };
    } catch (err) {
        return { success: false, error: handleSupabaseError(err) };
    }
};

/**
 * Get signed URL for private images (if needed in future)
 * @param {string} filePath - Path within the bucket
 * @param {number} expiresIn - Expiration time in seconds (default 1 hour)
 * @returns {Promise<{url: string|null, error: Object|null}>}
 */
export const getSignedUrl = async (filePath, expiresIn = 3600) => {
    try {
        const { data, error } = await supabase.storage
            .from(BUCKET_NAME)
            .createSignedUrl(filePath, expiresIn);

        if (error) {
            return { url: null, error: handleSupabaseError(error) };
        }

        return { url: data.signedUrl, error: null };
    } catch (err) {
        return { url: null, error: handleSupabaseError(err) };
    }
};
